import {
  ONLINE_AND_STAFF_AVAILABLE,
  useAppointmentOnlineStaffOnlySplit,
} from './useAppointmentOnlineStaffOnlySplit';
import { Appointment } from 'types/appointments';
import { APPOINTMENT_STATUS_FREE } from '../../../../types/appointmentsConst';
import { useEffect, useMemo, useState } from 'react';
import { errorToast } from '../../../../utils/toast';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';

export type OnlineOfflineType = 'STAFF_ONLY' | 'ONLINE';

const useFilterOnlineStaffOnlyAppointments = ({
  selectedSlotAppointments,
  passLockedId,
}: {
  passLockedId?: string;
  selectedSlotAppointments: Appointment[];
}) => {
  const freeAndLockedAppts = useMemo(
    () =>
      selectedSlotAppointments.filter(
        (a) => a.status === APPOINTMENT_STATUS_FREE || passLockedId === a.id,
      ),
    [passLockedId, selectedSlotAppointments],
  );

  const { onlineSplitStatus, freeOnlineAppointments, freeStaffAppointments } =
    useAppointmentOnlineStaffOnlySplit({
      appointments: freeAndLockedAppts,
    });

  const [selectedAppointmentType, setSelectedAppointmentType] =
    useState<OnlineOfflineType>('STAFF_ONLY');

  const filteredAppointments = useMemo(() => {
    if (onlineSplitStatus === ONLINE_AND_STAFF_AVAILABLE) {
      return selectedAppointmentType === 'ONLINE'
        ? freeOnlineAppointments
        : freeStaffAppointments;
    } else {
      return selectedSlotAppointments;
    }
  }, [
    freeOnlineAppointments,
    freeStaffAppointments,
    onlineSplitStatus,
    selectedSlotAppointments,
    selectedAppointmentType,
  ]);
  return {
    filteredAppointments,
    setSelectedAppointmentType,
    selectedAppointmentType,
    onlineSplitStatus,
  };
};

export const useSelectAppointmentToBook = ({
  selectedSlotAppointments,
  onError,
}: {
  selectedSlotAppointments: Appointment[];
  onError: () => void;
}) => {
  const { t } = useCommonTranslation();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>();
  const { filteredAppointments, ...rest } =
    useFilterOnlineStaffOnlyAppointments({
      passLockedId: selectedAppointmentId,
      selectedSlotAppointments,
    });

  useEffect(() => {
    const appt = filteredAppointments.find(
      ({ id }) => selectedAppointmentId === id,
    );
    if (!appt) {
      const freeApptId = filteredAppointments.find(
        (appt) => appt.status === 'FREE',
      )?.id;
      if (!freeApptId) {
        errorToast(t('appointment-error-all-appointments-locked'));
        onError();
        setSelectedAppointmentId(undefined);
      } else {
        setSelectedAppointmentId(freeApptId);
      }
    }
  }, [filteredAppointments, selectedAppointmentId, onError, t]);

  const selectedAppointment = useMemo(
    () => filteredAppointments.find(({ id }) => id === selectedAppointmentId),
    [filteredAppointments, selectedAppointmentId],
  );

  return {
    selectedAppointment,
    ...rest,
  };
};
